﻿import {translation} from "@2d-front-monorepo/core";
import {Button, Dialog, initializeMvvmContext, MvvmButton, MvvmDataProvider} from "@2d-front-monorepo/design-library";
import {PermitsMvvmErrorCollector} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, useCallback, useState} from "react";
import {Link, useNavigate} from "react-router";
import UserData from "../../content-blocks/UserData";
import UserPassword from "../../content-blocks/UserPassword";
import {reloadPermits} from "../../contexts/PermitsContext";
import {useGlobalContext} from "../../hooks/useGlobalContext";
import {useHttpClient} from "../../hooks/useHttpClient";
import {PrivateRoutes, PublicRoutes} from "../../routes";

type SignUpProps = {
	givenName: string;
	familyName: string;
	email: string;
	phone: string;
	password: string;
}

type DialogState = {
	visible: boolean;
	email: string;
}

export default function SignUp(): JSX.Element {
	let navigate = useNavigate();
	const global = useGlobalContext();
	const [dialogState, setDialogState] = useState<DialogState>({visible: false, email: ""});

	const context = initializeMvvmContext({
		initialState: () => {
			return {
				givenName: "",
				familyName: "",
				email: "",
				phone: "",
				password: ""
			};
		}
	});

	const onSubmit = useCallback(async function (data: SignUpProps): Promise<void> {
		const httpContext = useHttpClient();

		const result = await useHttpClient().authentication.isRegistered(global.installation.id, data.email, global.languageId);
		if (result.isRegistered) {
			setDialogState({visible: true, email: data.email});
			return;
		}

		setDialogState({visible: false, email: data.email});

		await httpContext.authentication.signUp({
			firstname: data.givenName,
			surname: data.familyName,
			email: data.email,
			password: data.password,
			installationId: global.installation.id,
			languageId: global.languageId,
			phone: {countryCode: global.installation.countryId, phone: data.phone}
		});

		await httpContext.authentication.signIn({
			email: data.email,
			password: data.password,
			installationId: global.installation.id,
			languageId: global.languageId
		});

		await reloadPermits();
		navigate(PrivateRoutes.permitsHome, {replace: true});
	}, []);

	return (
		<section className="sign-in full-layout full-layout--centered">
			<MvvmDataProvider<SignUpProps> context={context} onSubmit={onSubmit}>
				<h1>{translation(PermitTranslations.sign_up)}</h1>
				<div className="layout vertical gap">
					<PermitsMvvmErrorCollector/>

					<UserData global={global} context={context}/>
					<UserPassword context={context}/>

					<MvvmButton kind="primary">{translation(PermitTranslations.sign_up)}</MvvmButton>

					<p className="text-align--center">
						{translation(PermitTranslations.sign_up_page__already_have_account)}
						<Link to={PublicRoutes.signIn}>
							{translation(PermitTranslations.sign_up_page__log_into_account)}
						</Link>
					</p>
					<p><small>{translation(PermitTranslations.website__terms_and_conditions)}</small></p>
				</div>
			</MvvmDataProvider>
			{dialogState.visible &&
                <Dialog title={translation(PermitTranslations.sign_up_page__email_duplicated__title)}
                        onClose={() => setDialogState({visible: false, email: dialogState.email})}>
                    <p>{translation(PermitTranslations.sign_up_page__email_duplicated__body)}</p>
                    <div className="form__buttons form__buttons--bottom">
                        <Button small={true} kind="primary"
                                onClick={() => navigate(`${PublicRoutes.signIn}?email=${dialogState.email}`)}>
							{translation(PermitTranslations.sign_in)}
                        </Button>
                        <span></span>
                        <Button small={true}
                                onClick={() => navigate(`${PublicRoutes.forgotPassword}?email=${dialogState.email}`)}>
							{translation(PermitTranslations.sign_up_page__change_password)}
                        </Button>
                    </div>
                </Dialog>}
		</section>
	)
}
